/**
 * Centralized Event Tracking Utility
 *
 * This utility provides a single interface for tracking events across all platforms:
 * - Google Analytics (gtag)
 * - Facebook Pixel (Meta Pixel)
 * - Twitter Pixel
 * - Vercel Analytics
 *
 * Usage:
 *   import { TrackingEvents } from '@/lib/analytics'
 *   TrackingEvents.trackContactForm({ formId: 'project_123' })
 */

import { track } from "@vercel/analytics";
import { TwitterEvents } from "./twitter-events";
import { GOOGLE_LEAD_QUEUE_STORAGE_KEY, readConsentSnapshot } from "./consent";

const FORM_FUNNEL_STEPS = ["view", "start", "submit_attempt", "validation_error", "submit_error"];
const trackedLeadIds = new Set();

/**
 * Track Facebook Pixel event
 * @param {string} eventName - Facebook event name (e.g., 'Lead', 'CompleteRegistration', 'Contact')
 * @param {Object} eventData - Optional event data
 */
function trackFacebookEvent(eventName, eventData = {}) {
  if (!readConsentSnapshot().advertising) return;

  if (typeof window !== "undefined" && window.fbq) {
    try {
      if (eventData.lead_id) {
        window.fbq("track", eventName, eventData, { eventID: eventData.lead_id });
      } else {
        window.fbq("track", eventName, eventData);
      }

    } catch (error) {
      console.error("Error tracking Facebook event:", error);
    }
  } else {
    console.warn("Facebook Pixel not loaded, event not tracked");
  }
}

function claimConfirmedLead(formId) {
  if (!formId || trackedLeadIds.has(formId)) return false;
  trackedLeadIds.add(formId);
  return true;
}

/**
 * Track Google Analytics event
 * @param {string} eventName - GA event name
 * @param {Object} eventParams - Event parameters
 */
const LEAD_GOOGLE_EVENTS = new Set([
  "generate_lead", "home_assessment_form_submission",
  "business_assessment_form_submission", "contact_us_form_submission",
]);
const GOOGLE_RECEIPTS_KEY = GOOGLE_LEAD_QUEUE_STORAGE_KEY;

function readQueuedLeadEvents() {
  try {
    const records = JSON.parse(window.sessionStorage?.getItem(GOOGLE_RECEIPTS_KEY) || "[]");
    return Array.isArray(records) ? records.filter((value) => typeof value === "string").slice(-100) : [];
  } catch {
    return [];
  }
}

function trackGoogleAnalytics(eventName, eventParams = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return { outcome: "tag-unavailable" };
  }
  // This receipt means queued in gtag, never received/processed by Google.
  // Persist only with analytics consent; denied-mode pings retain existing behavior.
  const key = readConsentSnapshot().analytics && LEAD_GOOGLE_EVENTS.has(eventName) && eventParams.lead_id
    ? `${eventName}:${eventParams.lead_id}` : null;
  const receipts = key ? readQueuedLeadEvents() : [];
  if (key && receipts.includes(key)) return { outcome: "already-queued" };
  try {
    window.gtag("event", eventName, eventParams);
    if (key) {
      try {
        window.sessionStorage?.setItem(GOOGLE_RECEIPTS_KEY, JSON.stringify([...receipts, key].slice(-100)));
      } catch {
        // Restricted storage must never interrupt the submitted form.
      }
    }
    return { outcome: "queued" };
  } catch {
    return { outcome: "queue-error" };
  }
}

/**
 * Track Vercel Analytics event
 * @param {string} eventName - Event name
 * @param {Object} eventData - Event data
 */
function trackVercelAnalytics(eventName, eventData = {}) {
  if (!readConsentSnapshot().analytics) return;

  try {
    track(eventName, eventData);

  } catch (error) {
    console.error("Error tracking Vercel Analytics event:", error);
  }
}

/**
 * Map internal event names to platform-specific event names
 */
const EVENT_MAPPINGS = {
  // Form submissions
  form_submission: {
    facebook: "Lead",
    google: "generate_lead",
    vercel: "form_submission",
  },

  // Contact form
  contact_form: {
    facebook: "Lead",
    google: "generate_lead",
    googleCompatibility: "contact_us_form_submission",
    vercel: "form_submission",
    twitter: "trackContactForm",
  },

  // Home assessment form
  home_assessment_form: {
    facebook: "Lead",
    google: "generate_lead",
    googleCompatibility: "home_assessment_form_submission",
    vercel: "form_submission",
    twitter: "trackHomeAssessment",
  },

  // Home assessment funnel steps. These intentionally do not fire ad-platform
  // lead events; they are diagnostic events used to find form friction.
  home_assessment_start: {
    google: "home_assessment_start",
    vercel: "home_assessment_start",
  },
  home_assessment_optional_details: {
    google: "home_assessment_optional_details_opened",
    vercel: "home_assessment_optional_details_opened",
  },
  home_assessment_submit_attempt: {
    google: "home_assessment_submit_attempt",
    vercel: "home_assessment_submit_attempt",
  },
  home_assessment_validation_error: {
    google: "home_assessment_validation_error",
    vercel: "home_assessment_validation_error",
  },
  home_assessment_submit_error: {
    google: "home_assessment_submit_error",
    vercel: "home_assessment_submit_error",
  },
  home_assessment_dialog_open: {
    google: "home_assessment_dialog_open",
    vercel: "home_assessment_dialog_open",
  },
  home_assessment_dialog_close: {
    google: "home_assessment_dialog_close",
    vercel: "home_assessment_dialog_close",
  },

  // Discovery-tool engagement. These are navigation signals, not lead events.
  diagnostic_tools_open: {
    google: "diagnostic_tools_open",
    vercel: "diagnostic_tools_open",
  },
  diagnostic_tools_choice: {
    google: "diagnostic_tools_choice",
    vercel: "diagnostic_tools_choice",
  },
  diagnostic_tools_nudge_view: {
    google: "diagnostic_tools_nudge_view",
    vercel: "diagnostic_tools_nudge_view",
  },
  diagnostic_tools_nudge_dismiss: {
    google: "diagnostic_tools_nudge_dismiss",
    vercel: "diagnostic_tools_nudge_dismiss",
  },
  diagnostic_tool_entry_click: {
    google: "diagnostic_tool_entry_click",
    vercel: "diagnostic_tool_entry_click",
  },
  diagnostic_tools_help_me_choose_start: {
    google: "help_me_choose_start",
    vercel: "help_me_choose_start",
  },
  diagnostic_tools_help_me_choose_step: {
    google: "help_me_choose_step",
    vercel: "help_me_choose_step",
  },
  diagnostic_tools_help_me_choose_result: {
    google: "help_me_choose_result",
    vercel: "help_me_choose_result",
  },
  diagnostic_tools_help_me_choose_restart: {
    google: "help_me_choose_restart",
    vercel: "help_me_choose_restart",
  },
  diagnostic_tools_help_me_choose_product_click: {
    google: "help_me_choose_product_click",
    vercel: "help_me_choose_product_click",
  },

  // Business assessment form
  business_assessment_form: {
    facebook: "Lead",
    google: "generate_lead",
    googleCompatibility: "business_assessment_form_submission",
    vercel: "form_submission",
    twitter: "trackBusinessAssessment",
  },

  // Email subscription
  email_subscription: {
    facebook: "Subscribe",
    google: "email_subscription",
    vercel: "email_subscription",
    twitter: "trackNewsletterSubscription",
  },

  // Phone click
  phone_click: {
    facebook: "Contact",
    google: "phone_click",
    vercel: "phone_click",
  },
};

for (const step of FORM_FUNNEL_STEPS) {
  EVENT_MAPPINGS[`form_${step}`] = {
    google: `form_${step}`,
    vercel: `form_${step}`,
  };
}

/**
 * Main tracking function - tracks events across all platforms
 *
 * @param {string} eventType - Internal event type (e.g., 'form_submission', 'contact_form')
 * @param {Object} options - Tracking options
 * @param {Object} options.data - Event data to send to all platforms
 * @param {Object} options.platforms - Override which platforms to track (default: all)
 * @param {Object} options.customMappings - Custom event mappings for this call
 */
export function trackEvent(eventType, options = {}) {
  const { data = {}, platforms = {}, customMappings = {} } = options;

  // Get event mappings (custom or default)
  const mappings = customMappings[eventType] || EVENT_MAPPINGS[eventType];

  if (!mappings) {
    console.warn(`No event mapping found for: ${eventType}`);
    return;
  }

  // Track to Facebook Pixel
  if (platforms.facebook !== false && mappings.facebook) {
    const fbEventName = typeof mappings.facebook === "function" ? mappings.facebook(data) : mappings.facebook;
    trackFacebookEvent(fbEventName, data);
  }

  // Track to Google Analytics
  if (platforms.google !== false && mappings.google) {
    const gaEventName = typeof mappings.google === "function" ? mappings.google(data) : mappings.google;
    trackGoogleAnalytics(gaEventName, data);

    // Google Ads still imports the original form-specific GA4 events. Keep
    // sending them alongside generate_lead until the Ads goals are migrated.
    if (mappings.googleCompatibility) {
      const compatibilityEventName = typeof mappings.googleCompatibility === "function"
        ? mappings.googleCompatibility(data)
        : mappings.googleCompatibility;
      trackGoogleAnalytics(compatibilityEventName, data);
    }
  }

  // Track to Vercel Analytics
  if (platforms.vercel !== false && mappings.vercel) {
    const vercelEventName = typeof mappings.vercel === "function" ? mappings.vercel(data) : mappings.vercel;
    trackVercelAnalytics(vercelEventName, data);
  }

  // Track to Twitter Pixel
  if (
    platforms.twitter !== false &&
    mappings.twitter &&
    readConsentSnapshot().advertising &&
    TwitterEvents[mappings.twitter]
  ) {
    TwitterEvents[mappings.twitter](data);
  }
}

/**
 * Predefined tracking functions for common events
 */
export const TrackingEvents = {
  trackFormStep: (step, data = {}) => {
    if (!FORM_FUNNEL_STEPS.includes(step) || !data.form_type || !data.form_variant) return;
    if (step === "view" && !readConsentSnapshot().analytics) return;

    trackEvent(`form_${step}`, {
      data,
      platforms: {
        facebook: false,
        twitter: false,
      },
    });
  },
  /**
   * Track contact form submission
   * @param {Object} options - Tracking options
   * @param {string} options.formId - Form submission ID
   */
  trackContactForm: ({ formId, ...dimensions } = {}) => {
    if (!claimConfirmedLead(formId)) return;
    trackEvent("contact_form", {
      data: {
        form_type: "contact_us",
        form_id: formId,
        lead_id: formId,
        ...dimensions,
      },
    });
  },

  /**
   * Track home assessment form submission
   * @param {Object} options - Tracking options
   * @param {string} options.formId - Form submission ID
   * @param {string} options.programInterest - Program interest
   * @param {boolean} options.updated - Whether this was an update
   */
  trackHomeAssessment: ({ formId, programInterest, updated = false, ...dimensions } = {}) => {
    if (!claimConfirmedLead(formId)) return;
    trackEvent("home_assessment_form", {
      data: {
        form_type: "home_assessment",
        form_id: formId,
        lead_id: formId,
        action: updated ? "updated" : "created",
        program_interest: programInterest,
        ...dimensions,
      },
    });
  },

  trackHomeAssessmentStep: (step, data = {}) => {
    const eventType = `home_assessment_${step}`;
    trackEvent(eventType, {
      data: {
        form_type: "home_assessment",
        ...data,
      },
      platforms: {
        facebook: false,
        twitter: false,
      },
    });
  },

  trackDiagnosticTools: (action, data = {}) => {
    trackEvent(`diagnostic_tools_${action}`, {
      data: {
        event_category: "product_discovery",
        ...data,
      },
      platforms: {
        facebook: false,
        twitter: false,
      },
    });
  },

  trackDiagnosticToolEntry: ({ tool, placement, intent, pagePath } = {}) => {
    if (!tool || !placement || !intent) return;

    trackEvent("diagnostic_tool_entry_click", {
      data: {
        event_category: "product_discovery",
        tool,
        placement,
        intent,
        ...(pagePath ? { page_path: pagePath } : {}),
      },
      platforms: {
        facebook: false,
        twitter: false,
      },
    });
  },

  /**
   * Track business assessment form submission
   * @param {Object} options - Tracking options
   * @param {string} options.formId - Form submission ID
   */
  trackBusinessAssessment: ({ formId, ...dimensions } = {}) => {
    if (!claimConfirmedLead(formId)) return;
    trackEvent("business_assessment_form", {
      data: {
        form_type: "book_appointment",
        form_id: formId,
        lead_id: formId,
        ...dimensions,
      },
    });
  },

  /**
   * Track email subscription
   * @param {Object} options - Tracking options
   * @param {string} options.userId - User email
   * @param {Object} options.metadata - Additional metadata (source, discount_code, etc.)
   */
  trackEmailSubscription: ({ userId, metadata = {} } = {}) => {
    trackEvent("email_subscription", {
      data: {
        event_category: "engagement",
        ...metadata,
      },
      userId,
    });
  },

  /**
   * Track phone number click
   * @param {Object} options - Tracking options
   * @param {string} options.phoneNumber - Phone number clicked
   * @param {string} options.context - Context where click occurred
   */
  trackPhoneClick: ({ phoneNumber, context = "unknown" } = {}) => {
    trackEvent("phone_click", {
      data: {
        phone_number: phoneNumber,
        context,
      },
    });
  },

  /**
   * Track custom event
   * @param {string} eventType - Event type
   * @param {Object} options - Tracking options
   */
  trackCustom: (eventType, options = {}) => {
    trackEvent(eventType, options);
  },
};

/**
 * Backward-compatible functions for existing code
 */

/**
 * Track form submission across all platforms
 *
 * @deprecated Consider using TrackingEvents directly for better type safety
 * @param {string} formType - Type of form (e.g., 'contact', 'home-assessment', 'business-assessment')
 * @param {Object} additionalData - Additional data to track with the event
 */
export function trackFormSubmission(formType, additionalData = {}) {
  TrackingEvents.trackCustom("form_submission", {
    data: {
      form_type: formType,
      ...additionalData,
    },
  });
}

/**
 * Track phone number click across all platforms
 *
 * @param {string} phoneNumber - The phone number that was clicked
 * @param {string} context - Context where the click occurred (optional)
 */
export function trackPhoneClick(phoneNumber, context = null) {
  TrackingEvents.trackPhoneClick({
    phoneNumber,
    context: context || "unknown",
  });
}

/**
 * Export individual platform trackers for advanced use cases
 */
export { trackFacebookEvent, trackGoogleAnalytics, trackVercelAnalytics };
